/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form, Offcanvas } from 'react-bootstrap';
import { useState } from 'react';
import FeatherIcon from 'feather-icons-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faBarcode } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Button from 'components/base/Button';
import ProductCreateFilter from './ProductCreateFilter';
import reportInvoiceService from 'service/reportInvoice';
import { useWindowWidth } from 'utils/useWindowSize';

interface ProductFilterProps {
  setGeneratedTableData?: any;
  savedValues?: any;
  setSavedValues?: any;
  setGetName?: any;
  setGetBarcode?: any;
  getName?: any;
  getBarcode?: any;
  setGetFilterData?: any;
  getFilterData?: any;
  barcodeInputRef: any;
  setDebouncedBarcode?: any;
  setCurrentPage?: any;
  currentPage?: any;
  setPageCount?: any;
  setTotalAmount?: any;
  labeledVisibleFields?: any;
}

const ProductFilter = ({
  setGeneratedTableData,
  savedValues,
  setSavedValues,
  setGetName,
  setGetBarcode,
  getName,
  getBarcode,
  setGetFilterData,
  getFilterData,
  barcodeInputRef,
  setDebouncedBarcode,
  setCurrentPage,
  currentPage,
  setPageCount,
  setTotalAmount,
  labeledVisibleFields
}: ProductFilterProps) => {
  const url = process.env.REACT_APP_CDN_URL;
  // Initialize today's date in milliseconds
  const todayStart = new Date().setHours(0, 0, 0, 0);
  const todayEnd = new Date().setHours(0, 0, 0, 0);

  const currencyId = useSelector(
    (state: any) => state?.auth?.legal_entity?.currency_pid
  );

  const branchId = useSelector((state: any) => state?.auth?.legal_entity?.pid);
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const windowSize: any = useWindowWidth();

  const handleExport = () => {
    setLoading(true);
    const customFilename: string = 'Продажа по товарам.xlsx';

    // Prepare request payload
    const requestData = {
      name: getName,
      date_from:
        Object.keys(getFilterData).length === 0
          ? todayStart
          : getFilterData?.date_from,
      date_to:
        Object.keys(getFilterData).length === 0
          ? todayEnd + 86400000
          : getFilterData?.date_to,
      barcode: getBarcode,
      currency_pid:
        Object.keys(getFilterData).length === 0
          ? currencyId
          : getFilterData?.currency_pid,
      branch_pid:
        Object.keys(getFilterData).length === 0
          ? branchId
          : getFilterData?.branch_pid,
      visible_columns: labeledVisibleFields,
      ...getFilterData
    };

    // Fetch report and trigger download
    reportInvoiceService
      ?.getReportSaleListExcel(requestData)
      .then((res: any) => {
        setLoading(false);
        if (res?.filename !== '') {
          const resUrl = `${url}${res?.filename}`;
          const link = document.createElement('a');
          link.href = resUrl;
          link.download = customFilename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      })
      .catch(error => {
        setLoading(false);
        console.log('error==>', error);
      });
  };

  return (
    <>
      <Form
        className={
          windowSize > 768 ? 'd-flex gap-3' : 'd-flex flex-column gap-3'
        }
      >
        <div className="d-flex gap-2">
          <div className="form-icon-container">
            <Form.Floating>
              <Form.Control
                type="text"
                placeholder={t('enter_product')}
                onFocus={e => e.target.select()}
                onChange={e => setGetName(e.target.value)}
                className="form-icon-input"
              />
              <label
                htmlFor="floatingInputCustom"
                className="form-icon-label text-700"
              >
                {t('enter_product')}
              </label>
            </Form.Floating>
            <FontAwesomeIcon
              icon={faSearch}
              className="text-900 fs-9 form-icon"
            />
          </div>

          <div className="form-icon-container">
            <Form.Floating>
              <Form.Control
                ref={barcodeInputRef}
                type="text"
                placeholder={t('scan_barcode')}
                onFocus={e => e.target.select()}
                onChange={e => setGetBarcode(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault(); // Prevent form submission or unintended behavior
                    setCurrentPage(1);
                    setDebouncedBarcode(getBarcode);
                  }
                }}
                className="form-icon-input"
              />
              <label
                htmlFor="floatingInputCustom"
                className="form-icon-label text-700"
              >
                {t('scan_barcode')}
              </label>
            </Form.Floating>
            <FontAwesomeIcon
              icon={faBarcode}
              className="text-900 fs-9 form-icon"
            />
          </div>
        </div>

        <div className="d-flex gap-2">
          <Button
            style={{ height: '48px' }}
            variant="outline-primary"
            onClick={() => setOpenModal(true)}
          >
            <FeatherIcon icon="filter" className="me-2" size={20} />
            {t('filter')}
          </Button>

          <Button
            style={{ height: '48px' }}
            variant="outline-primary"
            onClick={handleExport}
            loading={loading}
            loadingPosition="start"
          >
            <FeatherIcon icon="upload-cloud" className="me-2" size={20} />
            {t('export')}
          </Button>
        </div>

        <Offcanvas
          show={openModal}
          onHide={() => {
            setOpenModal(false);
          }}
          placement="end"
          backdrop="static"
          keyboard={false}
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title style={{ fontSize: '24px', fontWeight: '600' }}>
              {t('filter')}
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <ProductCreateFilter
              setGeneratedTableData={setGeneratedTableData}
              savedValues={savedValues}
              setSavedValues={setSavedValues}
              setOpenModal={setOpenModal}
              getName={getName}
              getBarcode={getBarcode}
              setGetFilterData={setGetFilterData}
              currentPage={currentPage}
              setPageCount={setPageCount}
              setTotalAmount={setTotalAmount}
            />
          </Offcanvas.Body>
        </Offcanvas>
      </Form>
    </>
  );
};

export default ProductFilter;
